import { useEffect, useState } from "react";
import type { CuratorReading, CuratorReadingItem } from "@edufadder/shared-types";
import { Chip, spacing } from "@edufadder/ui";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";
import { ReadingCard } from "./FurtherReading.js";

/** Workbench controls for one claim's automatically found further reading. */
export function CuratorReadingPanel({ claimId, canEditQuery, canModerate }: { claimId: string; canEditQuery: boolean; canModerate: boolean }): JSX.Element {
  const [data, setData] = useState<CuratorReading | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .getCuratorReading(claimId)
      .then((d) => {
        setData(d);
        setQuery(d.reading_query);
      })
      .catch(() => setMessage("Couldn't load the further-reading list."));
  }, [claimId]);

  async function run(label: string, action: () => Promise<CuratorReading>, done: string): Promise<void> {
    setBusy(label);
    setMessage(null);
    try {
      const d = await action();
      setData(d);
      setQuery(d.reading_query);
      setMessage(done);
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : "That didn't work — please try again.");
    } finally {
      setBusy(null);
    }
  }

  function statusChip(item: CuratorReadingItem): JSX.Element {
    if (item.status === "pinned") return <Chip tone="green">Pinned (always shown)</Chip>;
    if (item.status === "hidden") return <Chip tone="red">Hidden from the public</Chip>;
    return item.vetted ? <Chip tone="blue">Shown automatically</Chip> : <Chip>No longer found</Chip>;
  }

  return (
    <div style={s.card}>
      <h2 style={s.heading2}>Further reading (found automatically)</h2>
      <p style={s.mutedText}>
        Open-access, peer-reviewed or journal articles and reviews in the top 10% most cited for their field and year,
        found in OpenAlex and ERIC and refreshed weekly. Pin an article to always show it; hide one that's off-topic or
        misleading. Refreshes never undo your choice.
      </p>

      <label style={s.label} htmlFor={`rq-${claimId}`}>
        Search phrases (separate with “;”)
      </label>
      <div style={{ display: "flex", gap: spacing(2), flexWrap: "wrap" }}>
        <input
          id={`rq-${claimId}`}
          style={{ ...s.input, flex: "1 1 300px", marginBottom: 0 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={!canEditQuery}
          placeholder="e.g. learning styles; meshing hypothesis"
          maxLength={500}
        />
        {canEditQuery && (
          <button
            type="button"
            style={s.buttonSecondary}
            disabled={busy !== null || query === data?.reading_query}
            onClick={() => void run("save", () => api.setReadingQuery(claimId, query), "Saved — the list will be refreshed with the new phrases.")}
          >
            Save phrases
          </button>
        )}
        {canModerate && (
          <button
            type="button"
            style={s.buttonPrimary}
            disabled={busy !== null}
            onClick={() => void run("refresh", () => api.refreshReading(claimId), "Refreshed from the catalogues.")}
          >
            {busy === "refresh" ? "Searching catalogues…" : "Refresh now"}
          </button>
        )}
      </div>
      <p style={{ ...s.mutedText, marginTop: spacing(2) }}>
        {data?.effective_phrases.length ? `Searching for: ${data.effective_phrases.map((p) => `“${p}”`).join(", ")}. ` : ""}
        {data?.refreshed_at ? `Last refreshed ${new Date(data.refreshed_at).toLocaleString()}.` : "Not refreshed yet."}
      </p>
      {message && <p style={s.mutedText} role="status">{message}</p>}

      {data && data.items.length === 0 && <p style={s.mutedText}>Nothing listed yet.</p>}
      {data && data.items.length > 0 && (
        <ul style={{ margin: 0, padding: 0 }}>
          {data.items.map((item) => (
            <ReadingCard
              key={item.id ?? item.title}
              item={item}
              footer={
                <div style={{ display: "flex", gap: spacing(2), alignItems: "center", flexWrap: "wrap", marginTop: spacing(2) }}>
                  {statusChip(item)}
                  {canModerate && item.id && (
                    <>
                      {item.status !== "pinned" && (
                        <button type="button" style={s.buttonSecondary} disabled={busy !== null}
                          onClick={() => void run(item.id!, () => api.setReadingStatus(claimId, item.id!, "pinned"), "Pinned.")}>
                          Pin
                        </button>
                      )}
                      {item.status !== "hidden" && (
                        <button type="button" style={s.buttonDanger} disabled={busy !== null}
                          onClick={() => void run(item.id!, () => api.setReadingStatus(claimId, item.id!, "hidden"), "Hidden from the public.")}>
                          Hide
                        </button>
                      )}
                      {item.status !== "auto" && (
                        <button type="button" style={s.buttonSecondary} disabled={busy !== null}
                          onClick={() => void run(item.id!, () => api.setReadingStatus(claimId, item.id!, "auto"), "Back to automatic.")}>
                          Reset
                        </button>
                      )}
                    </>
                  )}
                </div>
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}
